import { IMediaSchema, media } from '@monofe/streamlib';
import { injectMediaAPI } from '../api';
import mediaStreamBinder from '../api/impl/mediaStream.bind';
import { bindMediaAPI } from '../api/interface/bind';
import { IConfigCacheAssets, IConfigCacheName, injectConfigCache } from '../interface';
import { IFetcher, injectFetch } from '../interface/util';
import privateContainer from '../private/container';
import { injectMediaCacheController } from './interface';

const _mediaSchemaFetcher = async (
    fetcher: Promise<IFetcher>,
    schemaUrl: string,
): Promise<IMediaSchema | null> => {
    const fetchResolved = await fetcher;
    const fileContents = await fetchResolved<Object>({
        url: schemaUrl,
        expectedResponseType: 'json',
    });
    let mediaSchema = null;
    if (fileContents.payload) {
        mediaSchema = media.expand(JSON.stringify(fileContents.payload));
    }

    return mediaSchema;
};

async function bind(schemaUrl: string): Promise<void> {
    /* must load the private container, these assets are not global */
    const container = privateContainer();
    const fetcher = injectFetch(container);

    const schema = await _mediaSchemaFetcher(fetcher, schemaUrl);

    if (schema) {
        const mediaAPIs = injectMediaAPI(container) ?? [];
        const streamAPI = mediaStreamBinder(container, schema, schemaUrl);
        bindMediaAPI([streamAPI, ...mediaAPIs], container); /* make sure the streamAPI is first */

        const schemaPrefix = schemaUrl.replace('/media.json', '');

        const config = injectConfigCache(container);
        const configCache = config?.[IConfigCacheName.WorkspaceStream] as
            | IConfigCacheAssets
            | undefined;
        const streamCacheName = configCache?.cacheName ?? 'cache-streamMedia';

        const manager = injectMediaCacheController(container);
        manager.addCacheResolver(streamCacheName, (mediaId: string) => {
            const matchingItems = schema.media.filter(item => item.sourceGuid === mediaId);
            const urls = matchingItems.flatMap(item =>
                item.versions.map(version => `${schemaPrefix}${version.path}`),
            );
            return urls;
        });
    }

    return Promise.resolve();
}

export default bind;
